<template>
    <div class="h-full w-full create-report">
        <div class="h-full step-first mt-7 w-full">
            <div class="flex">
                <div>
                    <div class="field mb-5 flex align-items w-full report-name">
                        <div>
                            <div class="mb-2 flex items-center">
                                <i class="vc-icon-name label-icon text-primary text-xl" />
                                <span class="main-text-color">
                                    {{ $t('report.name') }}
                                </span>
                            </div>
                            <el-input :placeholder="$t('report.name')" v-model="report.ReportName"></el-input>
                            <div class="el-form-item__error" v-show="clickedOnNextBtn && checkIfValueIsEmpty(report.ReportName)">
                                {{ $t('validation.error.fieldIsRequired') }}
                            </div>
                        </div>
                    </div>
                    <div class="field-select-layout mb-5">
                        <LayoutSelect class="mt-4"
                            :label="$t('report.layout')"
                            :active-layout="{ LayoutID: report.LayoutID }"
                            @on-chose-layout="onChoseLayout"
                        />
                    </div>
                    <div class="field-select-language mb-5">
                        <div class="mb-2 flex items-center">
                            <i class="vc-icon-country label-icon text-primary text-xl" />
                            <span class="main-text-color">
                                {{ $t('report.language') }}
                            </span>
                        </div>
                        <el-select v-model="report.ReportLanguageID" class="w-full">
                            <el-option
                                v-for="(language, index) in languageList"
                                :key="`lang-${index}`"
                                :label="language.LanguageName"
                                :value="language.LanguageID"
                                class="language-item"
                            >
                            </el-option>
                        </el-select>
                    </div>
                    <div class="field-switch">
                        <div class="mb-2 flex items-center">
                            <i class="vc-icon-login-status label-icon text-primary text-xl" />
                            <span class="main-text-color">
                                {{ $t('report.status') }}
                            </span>
                        </div>
                        <div class="flex items-center">
                            <el-switch
                                v-model="report.ReportStatusID"
                                :active-text="report.ReportStatusID === REPORT_STATUS_ID_ENABLED ? $t('report.wizard.enabled') : $t('report.switch.disable')"
                                inactive-text=""
                                :active-value="1"
                                :inactive-value="2" />
                        </div>
                    </div>
                </div>
                <div>
                    <div class="flex flex-col pt-10" v-if="report.ReportConf">
                        <div class="flex items-end timezone-status">
                            <el-checkbox v-model="report.ReportConf.timezone.timezoneStatus" :true-label="1" :false-label="0">
                                {{ $t('report.timezone.status') }}
                            </el-checkbox>
                        </div>
                        <div class="timezone-select">
                            <div class="mb-2 flex items-center" v-if="report.ReportConf.timezone.timezoneStatus">
                                <i class="vc-icon-name label-icon text-primary text-xl" />
                                <span class="main-text-color">
                                    {{ $t('general.timezone') }}
                                </span>
                            </div>
                            <el-select v-if="report.ReportConf && report.ReportConf.timezone.timezoneStatus" :placeholder="$t('general.timezone')" filterable v-model="report.ReportConf.timezone.timezoneName" class="select-type">
                                <el-option :key="option.value" :value="option.value" :label="option.label" v-bind="option" v-for="option in timezones"/>
                            </el-select>
                        </div>
                    </div>
                </div>
            </div>
            <div class="pt-8 flex items-center w-full">
                <span class="icon-logo-voicenter">
                    <img :src="getLogo"
                        alt="Logo"
                        class="opacity-10 h-full max-h-52 object-cover"
                    >
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import {REPORT_STATUS_ID_ENABLED} from '@/enum/reportStatuses'
import {Checkbox, Option, Select} from 'element-ui'
import momentTimezone from 'moment-timezone'
import {languagesEnum} from "@/enum/languages.enum";
import {DEFAULT_LANGUAGE} from "@/enum/generic";

export default {
    components: {
        LayoutSelect: () => import('@/views/common/LayoutSelect'),
        [Select.name]: Select,
        [Option.name]: Option,
        [Checkbox.name]: Checkbox
    },
    props: {
        reportData: {
            type: Object,
            default: () => ({})
        }
    },
    data () {
        return {
            report: {
                ReportName: '',
                ReportStatusID: REPORT_STATUS_ID_ENABLED,
                LayoutID: null,
                ReportLanguageID: null
            },
            clickedOnNextBtn: false,
            REPORT_STATUS_ID_ENABLED,
            activeLayout: null
        }
    },
    computed: {
        languageList () {
            return [ ...languagesEnum ]
        },
        activeLanguage () {
            return this.$store.getters['lang/getActiveLanguageID'] || DEFAULT_LANGUAGE.LanguageID
        },
        selectedLayout () {
            return this.$store.getters['layout/getActiveLayout']
        },
        getLogo() {
            return this.$store.getters['layout/getLogo']('activeLayout')
        },
        timezones () {
            const timeZones = momentTimezone.tz.names();
            const offsetTmz=[];

            for(const i in timeZones) {
                offsetTmz.push({
                    value: timeZones[i],
                    label: "(GMT"+momentTimezone.tz(timeZones[i]).format('Z')+") " + timeZones[i]
                });
            }

            return offsetTmz
        },
        currentAccount() {
            return this.$store.getters['entities/getCurrentAccount']
        }
    },
    mounted () {
        const activeLayout = this.reportData.LayoutID || this.selectedLayout.LayoutID

        this.report = {
            ReportName: this.reportData.ReportName || this.$t('report.name'),
            ReportStatusID: this.reportData.ReportStatusID,
            AccountID: [this.$store.state.entities.selectedAccountID],
            LayoutID: activeLayout,
            ReportConf: this.reportData.ReportConf,
            ReportLanguageID: this.activeLanguage
        }

        this.$emit('updateReportData', this.report)
    },
    methods: {
        validate () {
            this.clickedOnNextBtn = true

            if (this.checkIfValuesIsEmpty()) {
                return
            }

            this.$emit('updateReportData', this.report)

            return true
        },
        checkIfValuesIsEmpty () {
            return Object.values(this.report).some(el => el === '' || el === null )
        },
        checkIfValueIsEmpty (value) {
            return value === '' || value === null
        },
        onChoseLayout(layout) {
            this.report.LayoutID = layout.LayoutID

            this.$emit('updateReportData', this.report)
        }
    },
    watch:{
        'report.ReportConf.timezone.timezoneStatus' (val) {
            if (!val) {
                this.report.ReportConf.timezone.timezoneName = null
                this.report.ReportConf.timezone.GmtOffset = this.currentAccount.GmtOffset
            }
        },
        'report.ReportConf.timezone.timezoneName' (val) {
            if (val) {
                const GmtOffset = momentTimezone().tz(val).utcOffset() * 3600
                this.report.ReportConf.timezone.GmtOffset = GmtOffset / 60
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.el-form-item__error {
    @apply pt-2;
}
.field, .field-switch, .field-select-layout, .field-select-language {
    @apply relative;
}

[dir="ltr"] .field, [dir="ltr"] .field-select-layout, [dir="ltr"] .field-select-language {
    @apply mr-8;
}
[dir="rtl"] .field, [dir="rtl"] .field-select-layout, [dir="rtl"] .field-select-language {
    @apply ml-8;
}
.field:not(:last-child) {
    width: 100%;
}
::v-deep .el-switch.is-checked .el-switch__core {
    border-color: var(--primary-color);
    background-color: var(--primary-color);
}
::v-deep .el-switch__label.is-active {
    @apply text-primary;
}
[dir="rtl"] .label-icon {
    @apply ml-2;
}
[dir="ltr"] .label-icon  {
    @apply mr-2;
}
::v-deep .el-switch__label {
    color: var(--mainTextColor-color);
}
</style>

<style lang="scss">
[dir="rtl"] .create-report .el-switch__label {
    @apply mr-2;
}
[dir="ltr"] .create-report .el-switch__label {
    @apply ml-2;
}
.layout-select-label {
    @apply text-base font-medium mx-2;
}
.layout-select-container {
    @apply mb-2;
}
.layout-selection {
    margin-top: 0px !important;
}
.field-select-layout .el-input__inner, .field-select-layout, .field-select-language, .report-name, .report-name .el-input__inner {
    width: 300px;
}
</style>

<style lang="scss" scoped>
.icon-logo-voicenter {
    width: 800px;
}
.timezone-select .el-input__inner {
    width: 350px;
}
.timezone-status {
    padding-bottom: 34px;
}
</style>
